import type { StorageAdapter } from "./storage";
import type { ConsentSnapshot } from "./types";

export function resolveConsentStorageScope(siteKey: string | undefined) {
  const normalized = siteKey?.trim();
  if (!normalized) return null;
  return {
    consentKey: `prospix_consent_${normalized}`,
    evidenceKey: `prospix_consent_evidence_${normalized}`,
    revocationKey: `prospix_consent_revocation_${normalized}`,
  };
}

export function normalizeSharedConsentSnapshot(
  value: unknown,
): ConsentSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    record.state !== "granted" &&
    record.state !== "denied" &&
    record.state !== "unknown"
  ) {
    return null;
  }
  const bounded = (candidate: unknown, max: number) =>
    typeof candidate === "string" ? candidate.slice(0, max) : undefined;
  const cmpRecord =
    record.cmp && typeof record.cmp === "object" && !Array.isArray(record.cmp)
      ? (record.cmp as Record<string, unknown>)
      : undefined;
  const source = bounded(cmpRecord?.source, 64);
  const purposesRecord =
    cmpRecord?.purposes &&
    typeof cmpRecord.purposes === "object" &&
    !Array.isArray(cmpRecord.purposes)
      ? (cmpRecord.purposes as Record<string, unknown>)
      : undefined;
  const tcfRecord =
    purposesRecord?.tcf &&
    typeof purposesRecord.tcf === "object" &&
    !Array.isArray(purposesRecord.tcf)
      ? (purposesRecord.tcf as Record<string, unknown>)
      : undefined;
  const hasTcf =
    purposesRecord !== undefined &&
    Object.prototype.hasOwnProperty.call(purposesRecord, "tcf");
  const tcfValid =
    !hasTcf ||
    (typeof tcfRecord?.["1"] === "boolean" &&
      typeof tcfRecord?.["3"] === "boolean" &&
      typeof tcfRecord?.["4"] === "boolean");
  const purposes =
    purposesRecord?.version === 1 &&
    typeof purposesRecord.analytics === "boolean" &&
    typeof purposesRecord.marketing === "boolean" &&
    tcfValid
      ? {
          version: 1 as const,
          analytics: purposesRecord.analytics,
          marketing: purposesRecord.marketing,
          ...(hasTcf && tcfRecord
            ? {
                tcf: {
                  "1": tcfRecord["1"] as boolean,
                  "3": tcfRecord["3"] as boolean,
                  "4": tcfRecord["4"] as boolean,
                },
              }
            : {}),
        }
      : undefined;
  const sectionIds = Array.isArray(cmpRecord?.gppSectionIds)
    ? cmpRecord.gppSectionIds
        .filter(
          (item): item is number =>
            typeof item === "number" && Number.isSafeInteger(item),
        )
        .slice(0, 64)
    : undefined;
  return {
    state: record.state,
    gpc: record.gpc === true,
    dnt: record.dnt === true,
    ...(source
      ? {
          cmp: {
            source,
            ...(source === "prospix_test_enrollment_v1"
              ? {
                  testEnrollmentToken: bounded(
                    cmpRecord?.testEnrollmentToken,
                    128,
                  ),
                  testEnrollmentBrowserId: bounded(
                    cmpRecord?.testEnrollmentBrowserId,
                    128,
                  ),
                  testEnrollmentExpiresAt:
                    typeof cmpRecord?.testEnrollmentExpiresAt === "number"
                      ? cmpRecord.testEnrollmentExpiresAt
                      : undefined,
                }
              : {}),
            tcString: bounded(cmpRecord?.tcString, 8192),
            uspString: bounded(cmpRecord?.uspString, 64),
            gppString: bounded(cmpRecord?.gppString, 8192),
            ...(sectionIds?.length ? { gppSectionIds: sectionIds } : {}),
            ...(purposes ? { purposes } : {}),
          },
        }
      : {}),
  };
}

export function readSharedConsentSnapshot(raw: string | null) {
  return readSharedConsentRecord(raw)?.snapshot ?? null;
}

export function readSharedConsentRecord(raw: string | null) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    const snapshot = normalizeSharedConsentSnapshot(parsed);
    if (!snapshot) return null;
    const record = parsed as Record<string, unknown>;
    return {
      snapshot,
      revocationToken:
        typeof record.revocationToken === "string"
          ? record.revocationToken.slice(0, 64)
          : null,
    };
  } catch {
    return null;
  }
}

export function resolveSharedConsentStorageState(
  rawRecord: string | null,
  legacyRevocationToken: string | null,
) {
  const record = readSharedConsentRecord(rawRecord);
  const atomicRevocationToken = record?.revocationToken ?? null;
  const fenceMismatch =
    legacyRevocationToken !== null &&
    atomicRevocationToken !== legacyRevocationToken;
  return {
    record,
    legacyRevocationToken,
    fenceMismatch,
    revocationToken: fenceMismatch
      ? null
      : (atomicRevocationToken ?? legacyRevocationToken),
  };
}

export function resolveSharedConsentSynchronization(options: {
  persistenceFailed: boolean;
  current: ConsentSnapshot;
  rawRecord: string | null;
  legacyRevocationToken: string | null;
}) {
  const state = resolveSharedConsentStorageState(
    options.rawRecord,
    options.legacyRevocationToken,
  );
  if (options.persistenceFailed || state.fenceMismatch) {
    const signalSnapshot = state.record?.snapshot ?? options.current;
    return {
      snapshot: buildStorageFailureConsentSnapshot(signalSnapshot),
      revocationToken: null,
      failClosed: true,
    };
  }
  return {
    snapshot: state.record?.snapshot ?? options.current,
    revocationToken: state.revocationToken,
    failClosed: false,
  };
}

export function persistSharedConsentRecord(
  storage: StorageAdapter,
  consentKey: string,
  legacyRevocationKey: string,
  record: ConsentSnapshot & {
    revocationToken: string | null;
    updatedAt: number;
  },
) {
  const serialized = JSON.stringify(record);
  // Rotate the compatibility fence first. Older token-only runtimes stop
  // immediately; current runtimes observe the temporary mismatch and also
  // fail closed until the atomic snapshot is published.
  if (record.revocationToken !== null) {
    storage.set(legacyRevocationKey, record.revocationToken);
  }
  const legacyVerified =
    record.revocationToken === null ||
    storage.get(legacyRevocationKey) === record.revocationToken;
  storage.set(consentKey, serialized);
  const atomicVerified = storage.get(consentKey) === serialized;
  const state = resolveSharedConsentStorageState(
    storage.get(consentKey),
    storage.get(legacyRevocationKey),
  );
  return {
    persisted: atomicVerified && legacyVerified && !state.fenceMismatch,
    state,
  };
}

export function sharedConsentSnapshotsEqual(
  left: ConsentSnapshot,
  right: ConsentSnapshot,
) {
  return (
    JSON.stringify(normalizeSharedConsentSnapshot(left)) ===
    JSON.stringify(normalizeSharedConsentSnapshot(right))
  );
}

export function hasUspSaleOptOut(snapshot: ConsentSnapshot) {
  const value = snapshot.cmp?.uspString;
  if (value === undefined) {
    // A declared USP signal with no usable evidence cannot authorize sale or
    // sharing. This matches the server ledger's fail-closed interpretation.
    return snapshot.cmp?.source?.trim().toLowerCase() === "usp";
  }
  return !/^1[YN][YN][YN]$/i.test(value) || value[2]?.toUpperCase() !== "N";
}

export function hasAuthoritativeMarketingPurposeEvidence(
  snapshot: ConsentSnapshot,
) {
  const source = snapshot.cmp?.source?.trim().toLowerCase();
  // The public host/theme consent API historically emits an explicit grant
  // without a CMP object. Preserve that narrow compatibility path; once a CMP
  // source is named, its bounded purpose evidence becomes authoritative.
  if (!source) return true;
  if (source === "gpp" || source === "prospix_ui") return false;
  const purposes = snapshot.cmp?.purposes;
  if (
    purposes?.version !== 1 ||
    purposes.analytics !== true ||
    purposes.marketing !== true
  ) {
    return false;
  }
  if (source !== "tcf") return true;
  return (
    purposes.tcf?.["1"] === true &&
    purposes.tcf?.["3"] === true &&
    purposes.tcf?.["4"] === true
  );
}

export function requiresConsentRevocationBinding(snapshot: ConsentSnapshot) {
  if (
    snapshot.state !== "granted" ||
    snapshot.gpc ||
    snapshot.dnt ||
    hasUspSaleOptOut(snapshot)
  ) {
    return true;
  }
  // Advertising remains denied, but an active, explicitly joined test has
  // separate activity/contact permission. Genuine restrictions above still win.
  if (
    snapshot.cmp?.source === "prospix_test_enrollment_v1" &&
    /^test-session-[a-f0-9]{64}$/.test(
      snapshot.cmp.testEnrollmentToken ?? "",
    ) &&
    typeof snapshot.cmp.testEnrollmentExpiresAt === "number" &&
    snapshot.cmp.testEnrollmentExpiresAt > Date.now()
  )
    return false;
  return !hasAuthoritativeMarketingPurposeEvidence(snapshot);
}

export function isConsentDeliveryAllowed(options: {
  snapshot: ConsentSnapshot;
  kind: "essential" | "analytics" | "marketing";
  consentMode?: "opt-in" | "opt-out" | "strict";
  respectGpc: boolean;
  respectDnt: boolean;
}) {
  if (options.kind === "essential") return true;
  // Trusted GPC and DNT signals are absolute browser-level opt-outs for this
  // release. Neither a stored decision nor a later CMP/manual grant may
  // authorize nonessential work while the signal remains active.
  if (options.snapshot.gpc || options.snapshot.dnt) return false;
  if (options.kind === "marketing" && hasUspSaleOptOut(options.snapshot)) {
    return false;
  }
  if (
    options.kind === "marketing" &&
    !hasAuthoritativeMarketingPurposeEvidence(options.snapshot)
  ) {
    // Purpose-specific host CMP evidence is required for high-risk browser
    // work. Built-in analytics, metadata-only GPP, and legacy/incomplete
    // Shopify or TCF snapshots cannot authorize identity or destinations.
    return false;
  }
  if (options.snapshot.state === "granted") return true;
  if (options.snapshot.state === "denied") return false;
  return options.consentMode === "opt-out";
}

export function buildStorageFailureConsentSnapshot(
  attempted: ConsentSnapshot,
): ConsentSnapshot {
  return {
    state: "denied",
    gpc: attempted.gpc,
    dnt: attempted.dnt,
  };
}

export function shouldHoldConsentPersistenceLatch(options: {
  persistenceFailed: boolean;
  signalSource?: string;
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
}) {
  if (!options.persistenceFailed) return false;
  if (options.signalSource === "manual") return false;
  return options.analyticsAllowed || options.marketingAllowed;
}

export function resolveConsentLifecycleActions(options: {
  analyticsWasAllowed: boolean;
  analyticsIsAllowed: boolean;
  marketingWasAllowed: boolean;
  marketingIsAllowed: boolean;
}) {
  return {
    revokeAnalytics: options.analyticsWasAllowed && !options.analyticsIsAllowed,
    grantAnalytics: !options.analyticsWasAllowed && options.analyticsIsAllowed,
    revokeMarketing: options.marketingWasAllowed && !options.marketingIsAllowed,
    grantMarketing: !options.marketingWasAllowed && options.marketingIsAllowed,
  };
}

export function resolveConsentSnapshotForSharedWrite(
  attempted: ConsentSnapshot,
  options: { fenceMismatch: boolean; signalSource?: string },
) {
  const signalEnforced =
    attempted.gpc || attempted.dnt
      ? { ...attempted, state: "denied" as const }
      : attempted;
  // Only a full nonessential stop is safe enough to repair a split fence.
  // Trusted GPC/DNT are full nonessential stops. A USP sale opt-out remains a
  // marketing-only restriction, so it may not resurrect a stale grant here.
  const hasFullDenial =
    signalEnforced.state === "denied" ||
    signalEnforced.gpc ||
    signalEnforced.dnt;
  const mustRepairAsDenied =
    options.fenceMismatch &&
    !hasFullDenial &&
    options.signalSource !== "manual";
  return {
    snapshot: mustRepairAsDenied
      ? buildStorageFailureConsentSnapshot(signalEnforced)
      : signalEnforced,
    failureSource: mustRepairAsDenied ? "storage_fence_mismatch" : undefined,
  };
}
